import ApexCharts, { ApexOptions } from "apexcharts";
import { FC, useEffect, useRef, useState } from "react";
import {
  getCSS,
  getCSSVariableValue,
} from "../../../_metronic/assets/ts/_utils";
import { Alert, Spinner } from "react-bootstrap-v5";
import axios from "axios";
import { KTSVG } from "../../../_metronic/helpers";
import { IntlShape, useIntl } from "react-intl";

type Props = {
  source: string;
  title: string;
  loading: boolean;
  data: any;
};

const NotificationStatsV2: FC<Props> = ({ source, title, loading, data }) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const intl = useIntl();
  const [hasData, setHasData] = useState(false);

  useEffect(() => {
    if (!data || data.length === 0) return;
    if (!chartRef.current) {
      return;
    }

    const totalNotifications = data
      ?.map((x: any) => x[source]?.notifications ?? 0)
      .reduce((a: number, b: number) => a + b, 0);
    setHasData(totalNotifications && totalNotifications > 0);

    if (!totalNotifications || totalNotifications === 0) return;

    const height = parseInt(getCSS(chartRef.current, "height"));

    const chart = new ApexCharts(
      chartRef.current,
      getChartOptions(height, data, source, intl)
    );
    if (chart) {
      chart.render();
    }

    return () => {
      if (chart) {
        chart.destroy();
      }
    };
  }, [chartRef, loading, data, hasData]);

  return (
    <div className="card h-500px">
      <div className="card-header border-0 pt-9">
        <div className="card-title m-0">
          <span className="card-label fw-bolder fs-3 mb-1">
            {intl.formatMessage(
              {
                id: "page.dashboard.notification-by-day.title",
                defaultMessage: `Notificações por dia - ${title}`,
              },
              { title }
            )}
          </span>
        </div>
      </div>

      <div className="card-body p-9 text-center">
        {loading ? (
          <div className="text-center">
            <span className="spinner-border spinner-border-lg spinner-border-primary align-middle"></span>
          </div>
        ) : (
          <>
            {(!data || !hasData) && !loading && (
              <Alert className="bg-light-primary border border-primary border-dashed d-flex flex-row">
                <div className="svg-icon svg-icon-2hx svg-icon-primary me-4 mb-5 mb-sm-0">
                  <KTSVG path="/media/icons/duotone/Home/Bulb1.svg" />
                </div>

                <div className="d-flex flex-column pe-0 pe-sm-10 flex-grow-1">
                  <h5>
                    {intl.formatMessage(
                      {
                        id: "page.dashboard.notification-by-day.no-data",
                        defaultMessage: `Sem notificações por ${title} no período selecionado`,
                      },
                      { title }
                    )}
                  </h5>
                  <div>
                    {intl.formatMessage({
                      id: "page.dashboard.notification-by-week.help-text-1",
                      defaultMessage: "Se precisar de ajuda",
                    })}
                    ,{" "}
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        (window as any).$crisp?.push(["do", "chat:open"]);
                      }}
                    >
                      {intl.formatMessage({
                        id: "page.dashboard.notification-by-week.help-text-2",
                        defaultMessage:
                          "fale com nosso time de customer success",
                      })}
                    </a>
                  </div>
                </div>
              </Alert>
            )}
            <div
              ref={chartRef}
              id="kt_charts_widget_1_chart"
              style={{ height: "350px" }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationStatsV2;

function getChartOptions(
  height: number,
  data: any[],
  source: string,
  intl: IntlShape
): ApexOptions {
  const hasCurrentPeriod = data.length > 0 && data[data.length - 1]._isCurrent === true;
  const getMaxBarValue = (item: any) => {
    if (source === "email") {
      return Math.max(
        item[source]?.notifications ?? 0,
        item[source]?.opens ?? 0,
        item[source]?.clicks ?? 0,
        item[source]?.productReviews ?? 0,
        item[source]?.storeReviews ?? 0
      );
    }
    return Math.max(
      item[source]?.notifications ?? 0,
      item[source]?.productReviews ?? 0,
      item[source]?.storeReviews ?? 0
    );
  };

  const maxBarSize = data.reduce((acc: number, item: any) => {
    return Math.max(acc, typeof item === "object" ? getMaxBarValue(item) : 0);
  }, 1);

  const maxLineSize = data.reduce((acc: number, item: any) => {
    if (typeof item !== "object") return acc;
    const convRate = (item[source]?.conversionRate ?? 0) * 100;
    const openRate = source === "email" ? (item[source]?.openRate ?? 0) * 100 : 0;
    const clickRate = source === "email" ? (item[source]?.clickRate ?? 0) * 100 : 0;
    return Math.max(acc, convRate, openRate, clickRate, 100);
  }, 100);

  const series =
    source === "email"
      ? [
          {
            name: intl.formatMessage({ id: "chart-sent", defaultMessage: "Enviadas" }),
            data: data.map((item: any) => item.email?.notifications ?? 0),
            type: "bar",
          },
          {
            name: intl.formatMessage({ id: "chart-open", defaultMessage: "Abertas" }),
            data: data.map((item: any) => item.email?.opens ?? 0),
            type: "bar",
          },
          {
            name: intl.formatMessage({ id: "chart-click", defaultMessage: "Clicadas" }),
            data: data.map((item: any) => item.email?.clicks ?? 0),
            type: "bar",
          },
          {
            name: intl.formatMessage({ id: "chart-product-reviews", defaultMessage: "Av. de produto" }),
            data: data.map((item: any) => item.email?.productReviews ?? 0),
            type: "bar",
          },
          {
            name: intl.formatMessage({ id: "chart-store-reviews", defaultMessage: "Av. de loja" }),
            data: data.map((item: any) => item.email?.storeReviews ?? 0),
            type: "bar",
          },
          {
            name: intl.formatMessage({ id: "chart-seller-reviews", defaultMessage: "Av. de seller" }),
            data: data.map((item: any) => item.email?.sellerReviews ?? 0),
            type: "bar",
          },
          {
            name: intl.formatMessage({ id: "chart-open-rate", defaultMessage: "Taxa de abertura" }),
            data: data.map((item: any) => ((item.email?.openRate ?? 0) * 100).toFixed(2)),
            type: "line",
          },
          {
            name: intl.formatMessage({ id: "chart-click-rate", defaultMessage: "Taxa de clique" }),
            data: data.map((item: any) => ((item.email?.clickRate ?? 0) * 100).toFixed(2)),
            type: "line",
          },
          {
            name: intl.formatMessage({ id: "chart-conversion-rate", defaultMessage: "Taxa de conversão" }),
            data: data.map((item: any) => ((item.email?.conversionRate ?? 0) * 100).toFixed(2)),
            type: "line",
          },
        ]
      : [
          {
            name: intl.formatMessage({ id: "chart-sent", defaultMessage: "Enviadas" }),
            data: data.map((item: any) => item.whatsapp?.notifications ?? 0),
            type: "bar",
          },
          {
            name: intl.formatMessage({ id: "chart-product-reviews", defaultMessage: "Av. de produto" }),
            data: data.map((item: any) => item.whatsapp?.productReviews ?? 0),
            type: "bar",
          },
          {
            name: intl.formatMessage({ id: "chart-store-reviews", defaultMessage: "Av. de loja" }),
            data: data.map((item: any) => item.whatsapp?.storeReviews ?? 0),
            type: "bar",
          },
          {
            name: intl.formatMessage({ id: "chart-seller-reviews", defaultMessage: "Av. de seller" }),
            data: data.map((item: any) => item.whatsapp?.sellerReviews ?? 0),
            type: "bar",
          },
          {
            name: intl.formatMessage({ id: "chart-conversion-rate", defaultMessage: "Taxa de conversão" }),
            data: data.map((item: any) => ((item.whatsapp?.conversionRate ?? 0) * 100).toFixed(2)),
            type: "line",
          },
        ];

  const barYAxis = {
    min: 0,
    max: maxBarSize,
    labels: { show: true, style: { fontSize: "12px" } },
  };

  const barYAxisHidden = {
    min: 0,
    max: maxBarSize,
    labels: { show: false },
  };

  const lineYAxis = {
    opposite: true,
    floating: true,
    min: 0,
    max: maxLineSize,
    labels: { show: true },
  };

  const lineYAxisHidden = {
    opposite: true,
    floating: true,
    min: 0,
    max: maxLineSize,
    labels: { show: false },
  };

  const notifFormatter = (val: any) =>
    intl.formatMessage({ id: "chart-notification-total", defaultMessage: `${val} notificações` }, { total: val });
  const reviewFormatter = (val: any) =>
    intl.formatMessage({ id: "chart-review-total", defaultMessage: `${val} avaliações` }, { total: val });
  const pctFormatter = (val: any) => val + "%";

  return {
    series,
    chart: {
      fontFamily: "inherit",
      type: "bar",
      height,
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
        export: {
          csv: {
            filename: `notificacoes-${source}`,
            headerCategory: "Data",
          },
        },
      },
    },
    forecastDataPoints: {
      count: hasCurrentPeriod ? 1 : 0,
      fillOpacity: 0.4,
      dashArray: 6,
    },
    plotOptions: {
      bar: { horizontal: false, columnWidth: "30%", borderRadius: 5 },
    },
    legend: { show: true },
    dataLabels: { enabled: false },
    stroke: { show: true, width: 2 },
    xaxis: {
      categories: data.map((item: any) => item.date ?? item.weekYear),
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { style: { fontSize: "12px" } },
    },
    yaxis:
      source === "email"
        ? [barYAxis, barYAxisHidden, barYAxisHidden, barYAxisHidden, barYAxisHidden, barYAxisHidden, lineYAxis, lineYAxisHidden, lineYAxisHidden]
        : [barYAxis, barYAxisHidden, barYAxisHidden, barYAxisHidden, lineYAxisHidden],
    fill: { opacity: 1 },
    states: {
      normal: { filter: { type: "none", value: 0 } },
      hover: { filter: { type: "none", value: 0 } },
      active: { allowMultipleDataPointsSelection: false, filter: { type: "none", value: 0 } },
    },
    tooltip: {
      style: { fontSize: "12px" },
      shared: true,
      intersect: false,
      y:
        source === "email"
          ? [
              { formatter: notifFormatter },
              { formatter: notifFormatter },
              { formatter: notifFormatter },
              { formatter: reviewFormatter },
              { formatter: reviewFormatter },
              { formatter: reviewFormatter },
              { formatter: pctFormatter },
              { formatter: pctFormatter },
              { formatter: pctFormatter },
            ]
          : [
              { formatter: notifFormatter },
              { formatter: reviewFormatter },
              { formatter: reviewFormatter },
              { formatter: reviewFormatter },
              { formatter: pctFormatter },
            ],
    },
    grid: {
      strokeDashArray: 4,
      yaxis: { lines: { show: true } },
    },
  };
}
